import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import Button from '@/components/ui/Button';
import Checkbox from '@/components/ui/Checkbox/Checkbox';
import { putAuth } from '@/api/ApiCalls';
import { useGetAuthApi } from '@/hooks/UseApiCalls';
import useAuthContext from '@/contexts/Auth/useAuthContext';
import Fund from '@/models/Fund';
import { Role } from '@/models/common/Role';
import { ManagedUserDtoIn } from '@/models/in/ManagedUserDtoIn';
import { ManagedUserAccessDtoOut } from '@/models/out/ManagedUserAccessDtoOut';
import CompactDialog from './CompactDialog';
import UserFundsField from './UserFundsField';
import { ASSIGNABLE_ROLES, getUserFullName } from './userHelpers';

const UPDATE_ACCESS_URL = 'users/update-access';

interface EditUserPermissionsDialogProps {
  open: boolean;
  user: ManagedUserDtoIn;
  onClose: () => void;
  onSuccess: () => void;
}

const EditUserPermissionsDialog = ({ open, user, onClose, onSuccess }: EditUserPermissionsDialogProps) => {
  const { t } = useTranslation();
  const { currentUser } = useAuthContext();

  const { data: funds } = useGetAuthApi<Fund[]>('funds');

  const [roles, setRoles] = useState<string[]>([]);
  const [fundIds, setFundIds] = useState<number[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setRoles(user.roles ?? []);
      setFundIds(user.fundIds ?? []);
      setError(null);
    }
  }, [open, user]);

  const toggleRole = (role: Role) => {
    setError(null);
    setRoles((current) => (current.includes(role) ? current.filter((r) => r !== role) : [...current, role]));
  };

  const handleSave = async () => {
    setError(null);
    setIsSubmitting(true);
    // update-access replaces both sets wholesale, so whatever is not listed here is revoked.
    const body: ManagedUserAccessDtoOut = { email: user.email, roles, fundIds };

    try {
      await putAuth<void, object, ManagedUserAccessDtoOut>(UPDATE_ACCESS_URL, { Authorization: `Bearer ${currentUser?.token}` }, {}, body);
      onSuccess();
      onClose();
    } catch (err) {
      // A refusal - a project where they are a lead coordinator - comes back in Hebrew, ready to show.
      const message = axios.isAxiosError(err) && typeof err.response?.data === 'string' ? err.response.data.trim() : '';
      setError(message || t('usersManagement.permissions.saveError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const isEditingSelf = user.email === currentUser?.email;
  const isRemovingOwnAdmin = isEditingSelf && !roles.includes(Role.Admin);

  return (
    <CompactDialog
      open={open}
      onClose={onClose}
      title={t('usersManagement.permissions.title')}
      wide
      footer={
        <>
          <Button variant="white" onClick={onClose} disabled={isSubmitting}>
            {t('cancel')}
          </Button>
          <Button variant="primary" onClick={handleSave} disabled={isSubmitting}>
            {isSubmitting ? t('saving') : t('save')}
          </Button>
        </>
      }
    >
      <div className="flex flex-col">
        <span className="body-lr-semibold">{getUserFullName(user) || t('usersManagement.noName')}</span>
        <span dir="ltr" className="body-sm-regular text-(--font-body-2) text-start">
          {user.email}
        </span>
      </div>

      <div className="flex flex-col gap-2">
        <span className="body-sm-semibold text-(--font-secondary)">{t('usersManagement.permissions.rolesLabel')}</span>
        {ASSIGNABLE_ROLES.map((role) => (
          <label key={role} className="flex items-start gap-3 cursor-pointer">
            <Checkbox checked={roles.includes(role)} onCheckedChange={() => toggleRole(role)} />
            <span className="flex flex-col">
              <span className="body-md-semibold text-(--font-body-1)">{t(`usersManagement.roles.${role}`)}</span>
              <span className="body-sm-regular text-(--font-body-2)">{t(`usersManagement.permissions.roleHint.${role}`)}</span>
            </span>
          </label>
        ))}
        {isRemovingOwnAdmin && <p className="body-sm-regular text-amber-700">{t('usersManagement.permissions.selfAdminWarning')}</p>}
      </div>

      <UserFundsField fundIds={fundIds} onChange={setFundIds} funds={funds} />
      {/* What taking a project off a coordinator does (3.11.2026) - known when the window opens, so it keeps its size. */}
      {(user.fundIds?.length ?? 0) > 0 && <p className="body-sm-regular text-(--font-body-2)">{t('usersManagement.permissions.fundsRemovalHint')}</p>}

      {error && <p className="text-red-500 body-sm-regular">{error}</p>}
    </CompactDialog>
  );
};

export default EditUserPermissionsDialog;
